import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Pencil, Share2, Flame, Tag, Plus, Lock, List, ListChecks } from "lucide-react";
import { useEstimate, projectCoins } from "@/context/EstimateContext";
import { usePro } from "@/context/ProContext";
import { FREE_PROJECT_LIMIT } from "@/config/pricing";
import { formatUSD, formatNum, rateLabel } from "@/lib/format";
import { BudgetRing } from "@/components/BudgetRing";
import { LineItemCard } from "@/components/LineItemCard";
import { AddLineItemDialog } from "@/components/AddLineItemDialog";
import { AddProjectDialog } from "@/components/AddProjectDialog";
import { toast } from "sonner";

const HERO =
  "https://images.unsplash.com/photo-1710438399422-2fca27686bcd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function ProjectDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getProject, projects, blendedRate, coinsPurchased, removeItem, hydrated } = useEstimate();
  const { isPro } = usePro();

  const [itemDialog, setItemDialog] = useState({ open: false, editing: null });
  const [nameDialog, setNameDialog] = useState(false);

  const project = getProject(id);
  const readOnly = !isPro && projects.length > FREE_PROJECT_LIMIT;

  useEffect(() => {
    // Wait for localStorage hydration before deciding a project is missing.
    if (!hydrated) return;
    if (!projects.some((p) => p.id === id)) navigate("/");
  }, [hydrated, projects, id, navigate]);

  if (!project) return <div className="min-h-screen bg-surface" data-testid="project-screen" />;

  const coins = projectCoins(project);
  const cost = coins * blendedRate;
  const pct = coinsPurchased > 0 ? coins / coinsPurchased : 0;

  const addTask = () => {
    if (readOnly) return navigate("/paywall");
    setItemDialog({ open: true, editing: null });
  };
  const editTask = (it) => {
    if (readOnly) return navigate("/paywall");
    setItemDialog({ open: true, editing: it });
  };
  const deleteTask = (iid) => {
    if (readOnly) return navigate("/paywall");
    removeItem(project.id, iid);
  };

  const handleShare = async () => {
    const lines = [
      `Estimate — ${project.name}`,
      "",
      `Coins: ${formatNum(coins)}`,
      `Rate: ${rateLabel(blendedRate)}`,
      `Cost: ${formatUSD(cost)}`,
      "",
      "Tasks:",
      ...project.items.map((it) => `- ${it.name}: ${formatNum(it.coins)} coins (${formatUSD(it.coins * blendedRate)})`),
    ];
    const text = lines.join("\n");
    try {
      if (navigator.share) await navigator.share({ title: project.name, text });
      else {
        await navigator.clipboard.writeText(text);
        toast.success("Estimate copied to clipboard");
      }
    } catch {
      /* dismissed */
    }
  };

  return (
    <div className="mx-auto max-w-2xl pb-16" data-testid="project-screen">
      <div className="hero-scrim rounded-b-2xl px-6 pb-7 pt-6" style={{ "--hero-img": `url(${HERO})` }}>
        <div className="mb-3 flex items-center justify-between">
          <button
            data-testid="project-back-button"
            onClick={() => navigate("/")}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15 text-white transition-colors hover:bg-white/25"
            aria-label="Back"
          >
            <ChevronLeft size={22} />
          </button>
          <div className="flex items-center gap-2">
            <button
              data-testid="edit-project-name-button"
              onClick={() => setNameDialog(true)}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15 text-white transition-colors hover:bg-white/25"
              aria-label="Rename"
            >
              <Pencil size={17} />
            </button>
            <button
              data-testid="share-button"
              onClick={handleShare}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15 text-white transition-colors hover:bg-white/25"
              aria-label="Share"
            >
              <Share2 size={18} />
            </button>
          </div>
        </div>
        <p className="mb-1 truncate font-display text-base font-bold text-white/90" data-testid="project-name">
          {project.name}
        </p>
        <div className="flex items-center justify-between gap-4">
          <h1 className="font-mono text-4xl font-bold text-white tabular truncate" data-testid="project-cost">
            {formatUSD(cost)}
          </h1>
          {coinsPurchased > 0 && <BudgetRing progress={pct} />}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="flex items-center gap-1.5 rounded-full bg-white/18 px-3 py-1 font-mono text-sm font-semibold text-white tabular">
            <Flame size={13} />
            <span data-testid="project-coins">{formatNum(coins)} coins</span>
          </span>
          <span className="flex items-center gap-1.5 rounded-full bg-white/18 px-3 py-1 font-mono text-sm font-semibold text-white tabular">
            <Tag size={13} />
            {rateLabel(blendedRate)}
          </span>
        </div>
      </div>

      <div className="px-4 pt-5 sm:px-6">
        {readOnly && (
          <button
            data-testid="project-locked-banner"
            onClick={() => navigate("/paywall")}
            className="mb-4 flex w-full items-center gap-2.5 rounded-xl border border-warning bg-surface-2 p-3 text-left"
          >
            <Lock size={18} className="text-warning" />
            <span className="text-sm font-medium">
              Editing locked — your Pro trial ended. Upgrade to edit, or keep just 1 project.
            </span>
          </button>
        )}

        <div className="mb-3 flex items-center justify-between">
          <p className="font-display text-xs font-bold tracking-wider text-muted-foreground">TASKS</p>
          {project.items.length > 0 && (
            <p className="font-mono text-xs font-semibold text-brand tabular">
              {project.items.length} item{project.items.length > 1 ? "s" : ""}
            </p>
          )}
        </div>

        {project.items.length === 0 ? (
          <div className="mb-2 flex items-center gap-3 rounded-xl bg-brand-tint p-4" data-testid="tasks-empty">
            <List size={22} className="shrink-0 text-brand" />
            <p className="text-sm leading-snug text-foreground/80">
              Add tasks. Each deducts coins and adds its cost — the total rolls up to the projects list.
            </p>
          </div>
        ) : (
          <>
            {project.items.map((item) => (
              <LineItemCard
                key={item.id}
                item={item}
                rate={blendedRate}
                onEdit={editTask}
                onDelete={deleteTask}
                readOnly={readOnly}
              />
            ))}
            <div
              className="mt-1 mb-2 flex items-center justify-between border-t border-border px-4 py-3"
              data-testid="tally-total"
            >
              <span className="font-mono text-sm font-semibold text-muted-foreground tabular">
                {formatNum(coins)} coins
              </span>
              <span className="font-mono text-xl font-bold text-brand tabular">{formatUSD(cost)}</span>
            </div>
          </>
        )}

        <button
          data-testid="add-line-item-button"
          onClick={addTask}
          className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-4 font-display font-bold text-white transition-colors hover:bg-brand-dark"
        >
          {readOnly ? <Lock size={18} /> : <Plus size={20} />}
          {readOnly ? "Upgrade to Edit" : "Add Line Item"}
        </button>
      </div>

      <AddLineItemDialog
        open={itemDialog.open}
        editing={itemDialog.editing}
        projectId={project.id}
        onOpenChange={(o) => setItemDialog((s) => ({ ...s, open: o }))}
      />
      <AddProjectDialog open={nameDialog} editing={project} onOpenChange={setNameDialog} />
    </div>
  );
}
